/**
 * pnl.ts — P&L taxonomy 26 บรรทัด + สูตร (CONTEXT §4) — pure compute (T072/T073)
 * ใช้ Decimal ทุกจำนวนเงิน; % ที่ฐานเป็นศูนย์คืน null (แสดง `—`)
 * VAT/WHT/tax-only ไม่รวมในนี้ (แยกทะเบียนภาษี); CIT ใช้ยอดจริงที่ส่งเข้ามา
 */
import { addMoney, subtractMoney, percentage, moneyToString, type MoneyInput, Decimal } from './money.js';

/** กลุ่มยอดที่เป็น input ให้ engine (จำนวนเงินเป็น string/number/Decimal) */
export interface PnlInput {
  sales: MoneyInput;
  otherIncome: MoneyInput;
  // costs of sale
  costOfProducts: MoneyInput;
  marketingExpenses: MoneyInput;
  commissionsPlatformFees: MoneyInput;
  transportationFulfillment: MoneyInput;
  // SG&A
  payrollEmployee: MoneyInput;
  accountingProfessional: MoneyInput;
  fixedCost: MoneyInput;
  variableCost: MoneyInput;
  otherOperating: MoneyInput;
  // below EBITDA
  depreciationAmortization: MoneyInput;
  interestFinance: MoneyInput;
  /** ปรับปรุงที่ยืนยันแล้ว ± ก่อนภาษี (default 0) */
  pbtAdjustments?: MoneyInput;
  /** ยอดจริง/adjustment จากฝ่ายบัญชี — ห้ามคำนวณ EBITDA×20% */
  corporateIncomeTax: MoneyInput;
}

export type PnlLineKey =
  | 'sales'
  | 'other_income'
  | 'total_income'
  | 'costs_of_sales'
  | 'cost_of_products'
  | 'marketing_expenses'
  | 'commissions_platform_fees'
  | 'transportation_fulfillment'
  | 'total_costs_of_sale'
  | 'gross_margin'
  | 'gp_pct'
  | 'payroll_employee'
  | 'accounting_professional'
  | 'fixed_cost'
  | 'variable_cost'
  | 'other_operating'
  | 'total_sga'
  | 'ebitda'
  | 'ebitda_pct'
  | 'depreciation_amortization'
  | 'ebit'
  | 'interest_finance'
  | 'profit_before_tax'
  | 'corporate_income_tax'
  | 'net_profit'
  | 'np_pct';

export interface PnlLine {
  order: number;
  key: PnlLineKey;
  label: string; // ป้ายแสดง (คงหัวข้อจากไฟล์อ้างอิง)
  /** ค่าเงินเป็น string (decimal); สำหรับบรรทัด % เป็น null */
  amount: string | null;
  /** ค่า % (เฉพาะบรรทัด GP%/EBITDA%/NP%); null = ฐานเป็นศูนย์ (`—`) */
  percent: number | null;
  kind: 'amount' | 'percent' | 'header';
}

/** ป้ายหัวข้อ P&L ตามไฟล์อ้างอิง (ใช้ร่วมกับหน้า mapping — T098) */
// ป้ายบรรทัด P&L: ภาษาไทยเป็นหลัก + วงเล็บอังกฤษไว้กระทบยอดกับ Excel/งบเดิม (CONTEXT §2 ใช้ไทยบนหน้าจอ)
export const PNL_LINE_LABELS: Record<PnlLineKey, string> = {
  sales: 'ยอดขาย (Sales)',
  other_income: 'รายได้อื่น (Other income)',
  total_income: 'รายได้รวม (Total income)',
  costs_of_sales: 'ต้นทุนขาย (Costs of sales)',
  cost_of_products: 'ต้นทุนสินค้า (Cost of products)',
  marketing_expenses: 'ค่าการตลาด (Marketing expenses)',
  commissions_platform_fees: 'ค่าคอมมิชชันและค่าธรรมเนียมแพลตฟอร์ม (Commissions & platform fees)',
  transportation_fulfillment: 'ค่าขนส่งและแพ็กสินค้า (Transportation & fulfillment)',
  total_costs_of_sale: 'รวมต้นทุนขาย (Total costs of sale)',
  gross_margin: 'กำไรขั้นต้น (Gross margin)',
  gp_pct: 'อัตรากำไรขั้นต้น % (GP %)',
  payroll_employee: 'เงินเดือนและสวัสดิการพนักงาน (Payroll & employee expenses)',
  accounting_professional: 'ค่าบัญชีและวิชาชีพ (Accounting & professional fees)',
  fixed_cost: 'ต้นทุนคงที่ (Fixed Cost)',
  variable_cost: 'ต้นทุนผันแปร (Variable Cost)',
  other_operating: 'ค่าใช้จ่ายดำเนินงานอื่น (Other operating expense)',
  total_sga: 'รวมค่าใช้จ่ายขายและบริหาร (Total SG&A)',
  ebitda: 'กำไรก่อนหักดอกเบี้ย ภาษี ค่าเสื่อม (EBITDA)',
  ebitda_pct: 'อัตรา EBITDA % (EBITDA %)',
  depreciation_amortization: 'ค่าเสื่อมราคาและค่าตัดจำหน่าย (Depreciation & amortization)',
  ebit: 'กำไรก่อนดอกเบี้ยและภาษี (EBIT)',
  interest_finance: 'ดอกเบี้ยและต้นทุนทางการเงิน (Interest & finance costs)',
  profit_before_tax: 'กำไรก่อนภาษี (Profit before tax)',
  corporate_income_tax: 'ภาษีเงินได้นิติบุคคล (Corporate income tax)',
  net_profit: 'กำไรสุทธิ (Net profit)',
  np_pct: 'อัตรากำไรสุทธิ % (NP %)',
};

/**
 * หมวดค่าใช้จ่าย (pnl_category) ที่ผู้ใช้เลือก → บรรทัด P&L
 * **ค่าที่เก็บใน DB ยังเป็นสตริงอังกฤษ (key ของ map นี้)** เพื่อไม่ให้ engine/ข้อมูลเดิมพัง
 * ส่วนการแสดงผลใช้ `pnlCategoryLabel()` เป็นภาษาไทย
 */
/** หมวดค่าใช้จ่าย (แสดงใน dropdown ของฟอร์มค่าใช้จ่าย) */
export const PNL_EXPENSE_CATEGORY_TO_LINE: Record<string, PnlLineKey> = {
  'Marketing expenses': 'marketing_expenses',
  'Commissions & platform fees': 'commissions_platform_fees',
  'Transportation & fulfillment': 'transportation_fulfillment',
  'Payroll & employee expenses': 'payroll_employee',
  'Accounting & professional fees': 'accounting_professional',
  'Fixed Cost': 'fixed_cost',
  'Variable Cost': 'variable_cost',
  'Other operating expense': 'other_operating',
};

/** ทุกหมวด pnl_category ที่รู้จัก (รวมรายได้อื่น) — ใช้สำหรับแปลป้ายแสดงผล */
export const PNL_CATEGORY_TO_LINE: Record<string, PnlLineKey> = {
  ...PNL_EXPENSE_CATEGORY_TO_LINE,
  'Other income': 'other_income',
};

/** ป้ายภาษาไทยของหมวด pnl_category (คืนค่าเดิมถ้าไม่รู้จัก) */
export function pnlCategoryLabel(value: string): string {
  const key = PNL_CATEGORY_TO_LINE[value];
  return key ? PNL_LINE_LABELS[key] : value;
}

/** ตัวเลือกหมวดค่าใช้จ่ายสำหรับ dropdown: value=อังกฤษ(เก็บจริง), label=ไทย */
export const PNL_CATEGORY_OPTIONS: { value: string; label: string }[] = Object.keys(
  PNL_EXPENSE_CATEGORY_TO_LINE,
).map((value) => ({ value, label: pnlCategoryLabel(value) }));

function pctToNumber(numerator: Decimal, denominator: Decimal): number | null {
  const p = percentage(numerator, denominator);
  return p === null ? null : Number(p.toDecimalPlaces(4).toString());
}

/** คำนวณ P&L 26 บรรทัดจาก input */
export function computePnl(input: PnlInput): PnlLine[] {
  const sales = new Decimal(moneyToString(input.sales));
  const otherIncome = new Decimal(moneyToString(input.otherIncome));
  const totalIncome = addMoney(sales, otherIncome);

  const costOfProducts = new Decimal(moneyToString(input.costOfProducts));
  const marketing = new Decimal(moneyToString(input.marketingExpenses));
  const commissions = new Decimal(moneyToString(input.commissionsPlatformFees));
  const transport = new Decimal(moneyToString(input.transportationFulfillment));
  const totalCostsOfSale = addMoney(costOfProducts, marketing, commissions, transport);

  const grossMargin = subtractMoney(totalIncome, totalCostsOfSale);

  const payroll = new Decimal(moneyToString(input.payrollEmployee));
  const accounting = new Decimal(moneyToString(input.accountingProfessional));
  const fixedCost = new Decimal(moneyToString(input.fixedCost));
  const variableCost = new Decimal(moneyToString(input.variableCost));
  const otherOp = new Decimal(moneyToString(input.otherOperating));
  const totalSga = addMoney(payroll, accounting, fixedCost, variableCost, otherOp);

  const ebitda = subtractMoney(grossMargin, totalSga);
  const depr = new Decimal(moneyToString(input.depreciationAmortization));
  const ebit = subtractMoney(ebitda, depr);
  const interest = new Decimal(moneyToString(input.interestFinance));
  const pbtAdj = new Decimal(moneyToString(input.pbtAdjustments ?? 0));
  const profitBeforeTax = addMoney(subtractMoney(ebit, interest), pbtAdj);
  const cit = new Decimal(moneyToString(input.corporateIncomeTax));
  const netProfit = subtractMoney(profitBeforeTax, cit);

  const amt = (key: PnlLineKey, order: number, v: Decimal): PnlLine => ({
    order,
    key,
    label: PNL_LINE_LABELS[key],
    amount: moneyToString(v),
    percent: null,
    kind: 'amount',
  });
  const pct = (key: PnlLineKey, order: number, num: Decimal): PnlLine => ({
    order,
    key,
    label: PNL_LINE_LABELS[key],
    amount: null,
    percent: pctToNumber(num, totalIncome),
    kind: 'percent',
  });
  const header = (key: PnlLineKey, order: number): PnlLine => ({
    order,
    key,
    label: PNL_LINE_LABELS[key],
    amount: null,
    percent: null,
    kind: 'header',
  });

  return [
    amt('sales', 1, sales),
    amt('other_income', 2, otherIncome),
    amt('total_income', 3, totalIncome),
    header('costs_of_sales', 4),
    amt('cost_of_products', 5, costOfProducts),
    amt('marketing_expenses', 6, marketing),
    amt('commissions_platform_fees', 7, commissions),
    amt('transportation_fulfillment', 8, transport),
    amt('total_costs_of_sale', 9, totalCostsOfSale),
    amt('gross_margin', 10, grossMargin),
    pct('gp_pct', 11, grossMargin),
    amt('payroll_employee', 12, payroll),
    amt('accounting_professional', 13, accounting),
    amt('fixed_cost', 14, fixedCost),
    amt('variable_cost', 15, variableCost),
    amt('other_operating', 16, otherOp),
    amt('total_sga', 17, totalSga),
    amt('ebitda', 18, ebitda),
    pct('ebitda_pct', 19, ebitda),
    amt('depreciation_amortization', 20, depr),
    amt('ebit', 21, ebit),
    amt('interest_finance', 22, interest),
    amt('profit_before_tax', 23, profitBeforeTax),
    amt('corporate_income_tax', 24, cit),
    amt('net_profit', 25, netProfit),
    pct('np_pct', 26, netProfit),
  ];
}
